import { useEffect, useMemo } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES, registrationEmirates } from "@services/referenceDataApi";
import { t } from "i18next";
import { FormProvider, useFieldArray } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import DriverDetails from "./DriverDetails";
import type {
  TruckDriverFormData,
  TruckGatepassOutletContext,
  TruckVehicleFormData,
} from "./types";

type VehicleType = {
  [key: string]: string;
};

const TRANSLATION_KEY = "gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

const createSeedDriver = (
  values?: Partial<
    Pick<
      TruckDriverFormData,
      "driverLicenseNumber" | "fullName" | "passportNumber" | "emiratesIdNumber" | "mobileNumber"
    >
  >,
): TruckDriverFormData => ({
  driverLicenseNumber: values?.driverLicenseNumber ?? "",
  fullName: values?.fullName ?? "",
  passportNumber: values?.passportNumber ?? "",
  emiratesIdNumber: values?.emiratesIdNumber ?? "",
  mobileNumber: values?.mobileNumber ?? "",
  securityClearanceId: "",
  securityClearanceValidity: "",
  securityClearanceFott: "",
});

const createSeedVehicle = (driver?: TruckDriverFormData): TruckVehicleFormData => ({
  registrationEmirate: "Fujairah",
  vehicleType: "",
  vehicleColour: "",
  category: "",
  plateNumber: "",
  drivers: [driver ?? createSeedDriver()],
});

const VehicleDetails = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => userRoles.includes(role));
  const { fields, append, remove } = useFieldArray({
    control: formData.control,
    name: "vehicles",
  });
  const { options: vehicleTypes } = useFormattedReference<VehicleType>(
    REFERENCE_DATA_TYPES.VEHICLE_TYPES,
    "bff-gatepass",
  );

  const formattedVehicleTypes = useMemo(
    () =>
      vehicleTypes.map((vehicleType) => ({
        value: vehicleType[Object.keys(vehicleType)[0]],
        label: vehicleType[Object.keys(vehicleType)[0]],
      })),
    [vehicleTypes],
  );

  const categoryOptions = useMemo(
    () => [
      { value: "S", label: "Small" },
      { value: "M", label: "Medium" },
      { value: "L", label: "Large" },
    ],
    [],
  );

  const visitorFullName = formData.watch("fullName") ?? "";
  const visitorPassportNumber = formData.watch("passportNumber") ?? "";
  const visitorEmiratesId = formData.watch("emiratesIdNumber") ?? "";
  const visitorPhoneNumber = formData.watch("phoneNumber") ?? "";
  const visitorDriverLicense = formData.watch("driverLicenseNumber") ?? "";
  const visitorScValidity = formData.watch("securityClearanceValidity") ?? "";
  const visitorScFott = formData.watch("securityClearanceFott") ?? "";
  const cargos = formData.watch("cargos") ?? [];
  const hasHazardCargo = cargos.some((cargo) => cargo?.hazardCargo === true);

  const syncVehiclesToStore = () => {
    updateFormData("vehicles", formData.getValues("vehicles"));
  };

  useEffect(() => {
    const currentVehicles = formData.getValues("vehicles");
    if (Array.isArray(currentVehicles) && currentVehicles.length > 0) return;

    const initialVehicles: TruckVehicleFormData[] = [
      createSeedVehicle(
        createSeedDriver({
          driverLicenseNumber: visitorDriverLicense,
          fullName: visitorFullName,
          passportNumber: visitorPassportNumber,
          emiratesIdNumber: visitorEmiratesId,
          mobileNumber: visitorPhoneNumber,
        }),
      ),
    ];

    formData.setValue("vehicles", initialVehicles, {
      shouldDirty: false,
      shouldValidate: false,
    });
    updateFormData("vehicles", initialVehicles);
  }, [
    formData,
    updateFormData,
    visitorDriverLicense,
    visitorFullName,
    visitorPassportNumber,
    visitorEmiratesId,
    visitorPhoneNumber,
  ]);

  useEffect(() => {
    const currentVehicles = formData.getValues("vehicles") ?? [];
    if (!Array.isArray(currentVehicles) || currentVehicles.length === 0) return;

    let hasAnyDriverChanged = false;
    const updatedVehicles = currentVehicles.map((vehicle, vehicleIndex) => {
      if (!isIndividualRole && vehicleIndex > 0) {
        return vehicle;
      }

      const currentDrivers = Array.isArray(vehicle.drivers) ? vehicle.drivers : [];
      const currentFirstDriver = currentDrivers[0] ?? createSeedDriver();

      const nextFirstDriver = {
        ...currentFirstDriver,
        fullName: visitorFullName,
        passportNumber: visitorPassportNumber,
        emiratesIdNumber: visitorEmiratesId,
        mobileNumber: visitorPhoneNumber,
        securityClearanceValidity: visitorScValidity,
        securityClearanceFott: visitorScFott,
      };

      const hasDriverChanged =
        currentDrivers.length === 0 ||
        currentFirstDriver.fullName !== nextFirstDriver.fullName ||
        currentFirstDriver.passportNumber !== nextFirstDriver.passportNumber ||
        currentFirstDriver.emiratesIdNumber !== nextFirstDriver.emiratesIdNumber ||
        currentFirstDriver.mobileNumber !== nextFirstDriver.mobileNumber ||
        (currentFirstDriver.securityClearanceValidity ?? "") !==
          (nextFirstDriver.securityClearanceValidity ?? "") ||
        (currentFirstDriver.securityClearanceFott ?? "") !==
          (nextFirstDriver.securityClearanceFott ?? "");

      if (!hasDriverChanged) {
        return vehicle;
      }

      hasAnyDriverChanged = true;
      return {
        ...vehicle,
        drivers: [nextFirstDriver, ...currentDrivers.slice(1)],
      };
    });

    if (!hasAnyDriverChanged) return;

    formData.setValue("vehicles", updatedVehicles, {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [
    formData,
    isIndividualRole,
    visitorFullName,
    visitorPassportNumber,
    visitorEmiratesId,
    visitorPhoneNumber,
    visitorScValidity,
    visitorScFott,
  ]);

  useEffect(() => {
    if (!hasHazardCargo) return;

    const currentVehicles = formData.getValues("vehicles") ?? [];
    const firstVehicle = currentVehicles[0] ?? createSeedVehicle();

    const firstDriver =
      (Array.isArray(firstVehicle.drivers) ? firstVehicle.drivers[0] : null) ??
      createSeedDriver({
        driverLicenseNumber: visitorDriverLicense,
        fullName: visitorFullName,
        passportNumber: visitorPassportNumber,
        emiratesIdNumber: visitorEmiratesId,
        mobileNumber: visitorPhoneNumber,
      });

    const shouldClamp =
      currentVehicles.length !== 1 ||
      !Array.isArray(firstVehicle.drivers) ||
      firstVehicle.drivers.length !== 1;

    if (!shouldClamp) return;

    const clampedVehicles = [{ ...firstVehicle, drivers: [firstDriver] }];

    formData.setValue("vehicles", clampedVehicles, {
      shouldDirty: true,
      shouldValidate: false,
    });
    updateFormData("vehicles", clampedVehicles);
  }, [formData, hasHazardCargo, updateFormData]);

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.vehicle-details`)}
        button={
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            disabled={hasHazardCargo && fields.length >= 1}
            label={
              <span className="normal-case font-medium">
                {t(`${TRANSLATION_KEY}.add-vehicle`, { defaultValue: "Add Vehicle" })}
              </span>
            }
            testId="truck-vehicle-add"
            onClick={() => {
              append(
                createSeedVehicle(
                  createSeedDriver({
                    driverLicenseNumber: isIndividualRole ? visitorDriverLicense : "",
                    fullName: isIndividualRole ? visitorFullName : "",
                    passportNumber: isIndividualRole ? visitorPassportNumber : "",
                    emiratesIdNumber: isIndividualRole ? visitorEmiratesId : "",
                    mobileNumber: isIndividualRole ? visitorPhoneNumber : "",
                  }),
                ),
              );
              syncVehiclesToStore();
            }}
          />
        }
      >
        <div className="col-span-2 flex flex-col gap-6">
          {fields.map((field, index) => (
            <div key={field.id} className="rounded border border-gray-200 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-sm font-semibold">
                  {t(`${TRANSLATION_KEY}.vehicle-details`)} {index + 1}
                </h4>
                {fields.length > 1 && index > 0 && (
                  <RemoveButton
                    startIcon={<CloseIcon />}
                    variant="contained"
                    disableElevation
                    className="normal-case"
                    label={<span className="normal-case font-medium">Remove</span>}
                    testId={`truck-vehicle-remove-${index}`}
                    onClick={() => {
                      remove(index);
                      syncVehiclesToStore();
                    }}
                  />
                )}
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-2">
                  <FormItem
                    required
                    name={`vehicles.${index}.registrationEmirate`}
                    type="autocomplete"
                    options={registrationEmirates}
                    label={t(`${TRANSLATION_KEY}.registration-emirate`)}
                    testId={`truckGatepass-registrationEmirate-${index}`}
                    onFieldChange={syncVehiclesToStore}
                  />
                </div>
                <div className="hidden md:block md:col-span-2" />
                <FormItem
                  required
                  name={`vehicles.${index}.vehicleType`}
                  type="autocomplete"
                  label={t(`${TRANSLATION_KEY}.vehicle-type`)}
                  options={formattedVehicleTypes}
                  testId={`truckGatepass-vehicleType-${index}`}
                  onFieldChange={syncVehiclesToStore}
                />
                <FormItem
                  required
                  name={`vehicles.${index}.vehicleColour`}
                  type="text"
                  maxChars={50}
                  label={t(`${TRANSLATION_KEY}.vehicle-colour`)}
                  testId={`truckGatepass-vehicleColour-${index}`}
                  onFieldChange={syncVehiclesToStore}
                />
                <FormItem
                  required
                  name={`vehicles.${index}.category`}
                  type="autocomplete"
                  options={categoryOptions}
                  label={t(`${TRANSLATION_KEY}.category`)}
                  testId={`truckGatepass-category-${index}`}
                  onFieldChange={syncVehiclesToStore}
                />
                <FormItem
                  required
                  name={`vehicles.${index}.plateNumber`}
                  type="text"
                  maxChars={50}
                  label={t(`${TRANSLATION_KEY}.vehicle-plate-number`)}
                  testId={`truckGatepass-plateNumber-${index}`}
                  onFieldChange={syncVehiclesToStore}
                />
                <DriverDetails
                  index={index}
                  onFieldChange={syncVehiclesToStore}
                  disableAddDriver={hasHazardCargo || isIndividualRole}
                  autoPopulateFirstDriverAcrossVehicles={isIndividualRole}
                />
              </div>
            </div>
          ))}
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VehicleDetails;
